import type React from 'react';
import { useState } from 'react';
import { createPartner, PartnerApiError } from '@/api/partners';
import {
  emptyPartnerForm,
  PartnerForm,
  type PartnerFormState,
} from '@/components/partners/partner-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  flattenZodErrors,
  parseMetadataInput,
  partnerFormSchema,
} from '@/schemas/partner-form-schema';

interface AddPartnerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddPartnerModal: React.FC<AddPartnerModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<PartnerFormState>(emptyPartnerForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [metadataError, setMetadataError] = useState<string | undefined>();
  const [rootError, setRootError] = useState<string | null>(null);

  const setField = <K extends keyof PartnerFormState>(key: K, value: PartnerFormState[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear the touched field's error so corrections give immediate feedback
    // rather than leaving a stale message until the next submit.
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
    if (key === 'metadata') setMetadataError(undefined);
  };

  const resetForm = () => {
    setValues(emptyPartnerForm());
    setFieldErrors({});
    setMetadataError(undefined);
    setRootError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setMetadataError(undefined);
    setRootError(null);

    const metadata = parseMetadataInput(values.metadata);
    if (!metadata.ok) {
      setMetadataError(metadata.error);
      return;
    }

    const parsed = partnerFormSchema.safeParse({
      partner_name: values.partner_name,
      partner_code: values.partner_code,
      party_role_id: values.party_role_id === '' ? undefined : values.party_role_id,
      party_status_id: values.party_status_id,
      metadata: metadata.value,
    });
    if (!parsed.success) {
      setFieldErrors(flattenZodErrors(parsed.error));
      return;
    }

    try {
      setSubmitting(true);
      await createPartner({
        partner_name: parsed.data.partner_name,
        partner_code: parsed.data.partner_code,
        party_role_id: parsed.data.party_role_id,
        party_status_id: parsed.data.party_status_id,
        metadata: parsed.data.metadata,
      });
      resetForm();
      onSuccess();
      onOpenChange(false);
    } catch (err: unknown) {
      if (err instanceof PartnerApiError && err.status === 409) {
        const msg =
          (typeof err.body?.message === 'string' && err.body.message) ||
          `Partner with code '${parsed.data.partner_code}' already exists`;
        setFieldErrors((prev) => ({ ...prev, partner_code: msg }));
      } else {
        setRootError(err instanceof Error ? err.message : 'Failed to create partner');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Add Partner</DialogTitle>
          <DialogDescription>
            Create a new partner entity (reseller, agent, integration partner, or partnership).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <PartnerForm
            idPrefix="add"
            values={values}
            onChange={setField}
            fieldErrors={fieldErrors}
            metadataError={metadataError}
          />
          {rootError && <p className="text-sm text-destructive">{rootError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
